/**
 * Demo-only "Run the Vitalora 3-asset review" trigger card.
 *
 * Lives on /settings so it's available for dry-runs without polluting
 * the Ask Puntt home with a button real prospects would see. Same
 * payload as the original home-page button: three seeded packshots
 * (luminos-spf30, vitacore-400g, vitacore-800g) attached via their
 * public dev/CDN URLs, sent with the canonical killer-flow trigger
 * prompt. The backend matcher in
 * `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`
 * routes this into `runKillerReviewFlow` because the message contains
 * "review" + the attachments are image-typed.
 *
 * The salesperson's real flow is to drag-and-drop their own files into
 * the composer; this card is the testing affordance for verifying the
 * staged 3-column reveal without doing that dance.
 */
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import { useCreateAskPunttChat } from "../../../utils/queries/askPuntt";
import { askPunttChatRoute } from "../../ask-puntt/chatRoute";

const VITALORA_DEMO_TRIGGER_PROMPT =
  "Review these three assets in parallel for brand, agency, regulatory, and medical.";

const VITALORA_DEMO_ASSETS: { path: string; name: string }[] = [
  { path: "/seed/vitalora/luminos-spf30.jpg", name: "luminos-spf30.jpg" },
  { path: "/seed/vitalora/vitacore-400g.jpg", name: "vitacore-400g.jpg" },
  { path: "/seed/vitalora/vitacore-800g.jpg", name: "vitacore-800g.jpg" },
];

export function DemoKillerFlowCard() {
  const { t } = useTranslation("ui");
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const analytics = useAnalytics("settings_demo_killer_flow");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onClick = async () => {
    if (isLoading || createChat.isPending) return;
    setIsLoading(true);
    setErrorMsg(null);
    analytics.capture("settings_demo_killer_flow_clicked", {
      asset_count: VITALORA_DEMO_ASSETS.length,
    });
    try {
      const attachments = VITALORA_DEMO_ASSETS.map((a) => ({
        key: `seed/${a.name}`,
        source: new URL(a.path, window.location.origin).toString(),
        type: "image" as const,
        name: a.name,
      }));
      const result = await createChat.mutateAsync({
        message: VITALORA_DEMO_TRIGGER_PROMPT,
        workflowType: "project",
        attachments,
      });
      analytics.capture("settings_demo_killer_flow_chat_created", {
        chat_id: result.chat._id,
        attachment_count: attachments.length,
      });
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (error) {
      analytics.capture("settings_demo_killer_flow_error", {
        ...errorAnalyticsPayload(error),
      });
      setErrorMsg(
        "Couldn't start the review. Try again, or check the console for details.",
      );
      console.error("DemoKillerFlowCard failed:", error);
      setIsLoading(false);
    }
  };

  return (
    <Card data-testid="settings-card-demo-killer-flow">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            {t("settings.demoKillerFlowCard.runCanned3ProductReview")}
          </Heading>
          <Text size="2" color="gray">
            Plays the headline 90-second demo moment: three Vitalora product
            images get reviewed in parallel by every AI reviewer, with findings
            appearing as they go. Use this to rehearse the demo without dragging
            files into Ask Puntt yourself. Same result as dropping the three
            images into the chat manually.
          </Text>
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="settings-demo-killer-flow-button"
            onClick={onClick}
            disabled={isLoading || createChat.isPending}
          >
            {isLoading || createChat.isPending
              ? t("settings.demoKillerFlowCard.startingReview")
              : t("settings.demoKillerFlowCard.runReview")}
          </Button>
          {errorMsg ? (
            <Text size="2" color="red">
              {errorMsg}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
